import type {
  CustomerInfo,
  Offering,
  Package,
  PurchaseResult,
  Purchases as PurchasesClient
} from '@revenuecat/purchases-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { apiRequest } from '../api/client';
import { useSession } from '../auth/SessionProvider';

interface RevenueCatCustomerConfig {
  appUserId: string;
  entitlementId: string;
  offeringId: string;
  productId: string;
}

interface RevenueCatValue {
  configured: boolean;
  loading: boolean;
  customerInfo: CustomerInfo | null;
  entitled: boolean;
  refreshCustomerInfo: () => Promise<CustomerInfo>;
  purchaseProduct: (customerEmail?: string) => Promise<PurchaseResult>;
}

const RevenueCatContext = createContext<RevenueCatValue | null>(null);
const PUBLIC_SDK_KEY = import.meta.env.VITE_REVENUECAT_PUBLIC_SDK_KEY?.trim();
type RevenueCatSdk = typeof import('@revenuecat/purchases-js');
let sdkPromise: Promise<RevenueCatSdk> | null = null;

function loadSdk(): Promise<RevenueCatSdk> {
  sdkPromise ??= import('@revenuecat/purchases-js');
  return sdkPromise;
}

async function purchasesFor(config: RevenueCatCustomerConfig): Promise<PurchasesClient> {
  if (!PUBLIC_SDK_KEY) {
    throw new Error('RevenueCat não está configurado neste ambiente.');
  }
  const { LogLevel, Purchases } = await loadSdk();

  if (!Purchases.isConfigured()) {
    Purchases.setLogLevel(import.meta.env.DEV ? LogLevel.Debug : LogLevel.Error);
    return Purchases.configure({
      apiKey: PUBLIC_SDK_KEY,
      appUserId: config.appUserId,
      flags: { autoCollectUTMAsMetadata: true }
    });
  }

  return Purchases.getSharedInstance();
}

export function packageForProduct(offering: Offering, productId: string): Package | null {
  return (
    offering.availablePackages.find(
      (candidate) => candidate.webBillingProduct.identifier === productId
    ) ?? null
  );
}

export function RevenueCatProvider({ children }: { children: ReactNode }): JSX.Element {
  const { session } = useSession();
  const [config, setConfig] = useState<RevenueCatCustomerConfig | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session || !PUBLIC_SDK_KEY) {
      setConfig(null);
      setCustomerInfo(null);
      return;
    }

    let active = true;
    setLoading(true);
    void apiRequest<RevenueCatCustomerConfig>('/v1/revenuecat/customer')
      .then(async (nextConfig) => {
        if (!active) return;
        const purchases = await purchasesFor(nextConfig);
        if (purchases.getAppUserId() !== nextConfig.appUserId) {
          await purchases.changeUser(nextConfig.appUserId);
        }
        const info = await purchases.getCustomerInfo();
        if (active) {
          setConfig(nextConfig);
          setCustomerInfo(info);
        }
      })
      .catch(() => {
        if (active) {
          setConfig(null);
          setCustomerInfo(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [session?.user.id]);

  const refreshCustomerInfo = useCallback(async () => {
    if (!config) throw new Error('RevenueCat ainda não está pronto.');
    const info = await (await purchasesFor(config)).getCustomerInfo();
    setCustomerInfo(info);
    return info;
  }, [config]);

  const purchaseProduct = useCallback(
    async (customerEmail?: string) => {
      if (!config) throw new Error('RevenueCat ainda não está pronto.');
      const purchases = await purchasesFor(config);
      const offerings = await purchases.getOfferings({ offeringIdentifier: config.offeringId });
      const offering = offerings.all[config.offeringId] ?? offerings.current;
      if (!offering) throw new Error('A oferta de upgrade ainda não foi publicada.');
      const rcPackage = packageForProduct(offering, config.productId);
      if (!rcPackage) throw new Error('O produto de upgrade não está disponível nesta oferta.');

      try {
        const result = await purchases.purchase({ rcPackage, customerEmail });
        setCustomerInfo(result.customerInfo);
        return result;
      } catch (error) {
        const { ErrorCode, PurchasesError } = await loadSdk();
        if (error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError) {
          throw new RevenueCatCancelledError();
        }
        throw error;
      }
    },
    [config]
  );

  const value = useMemo<RevenueCatValue>(
    () => ({
      configured: config !== null,
      loading,
      customerInfo,
      entitled: Boolean(config && customerInfo?.entitlements.active[config.entitlementId]),
      refreshCustomerInfo,
      purchaseProduct
    }),
    [config, loading, customerInfo, refreshCustomerInfo, purchaseProduct]
  );

  return <RevenueCatContext.Provider value={value}>{children}</RevenueCatContext.Provider>;
}

export function useRevenueCat(): RevenueCatValue {
  const value = useContext(RevenueCatContext);
  if (!value) throw new Error('useRevenueCat precisa estar dentro de RevenueCatProvider');
  return value;
}

export class RevenueCatCancelledError extends Error {
  constructor() {
    super('Compra cancelada.');
    this.name = 'RevenueCatCancelledError';
  }
}
